/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Box,
    Button,
    Accordion,
    AccordionButton,
    AccordionItem,
    AccordionPanel,
    Flex,
    useToast,
    useStyleConfig,
    Image
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm} from 'react-hook-form'
import PromoCodeFields from '@salesforce/retail-react-app/app/components/forms/promo-code-fields'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {
    useAccessToken,
    useCommerceApi,
    useShopperBasketsMutation
} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {PAYMENT_METHODS} from '../../api/utils/constants'
import {CheckIcon} from '../icons'
import {getUrl} from '../../utils/cloudinary-utils'
import {IdmeButton} from '../../components/idme'
import {useGlobaleContext} from '../../contexts/globale'
import useTagManager from '../../hooks/use-tag-manager'
import CustomApi from '../../hooks/use-custom-api'

export const usePromoCode = () => {
    const {formatMessage} = useIntl()
    const {data: basket} = useCurrentBasket()
    const form = useForm()
    const {countryISO: countryISOContext, currencyCode: currencyCodeContext} = useGlobaleContext()
    const toast = useToast()
    const tagManager = useTagManager()
    const customapi = new CustomApi()
    const api = useCommerceApi()
    const {proxy} = api.shopperExperience.clientConfig
    const {getTokenWhenReady} = useAccessToken()

    const applyPromoCodeMutation = useShopperBasketsMutation('addCouponToBasket')
    const removePromoCodeMutation = useShopperBasketsMutation('removeCouponFromBasket')
    const updateBasket = useShopperBasketsMutation('updateBasket')
    const {mutateAsync: updatePaymentInstrumentInBasket} = useShopperBasketsMutation(
        'updatePaymentInstrumentInBasket'
    )

    const isBoltShopperLoggedIn =
        typeof window !== 'undefined' && sessionStorage.getItem('isBoltShopperLoggedIn') === 'true'
    const hasBoltPayment = basket?.paymentInstruments?.some(
        (pi) => pi.paymentMethodId === PAYMENT_METHODS.BOLT_PAY
    )
    const isBolt = isBoltShopperLoggedIn && hasBoltPayment

    const updateBoltPaymentAmount = async () => {
        const token = await getTokenWhenReady()
        const freshBasket = await api.shopperBaskets.getBasket({
            parameters: {basketId: basket.basketId},
            headers: {Authorization: `Bearer ${token}`}
        })

        const boltInstrument = freshBasket?.paymentInstruments?.find(
            (pi) => pi.paymentMethodId === PAYMENT_METHODS.BOLT_PAY
        )
        if (boltInstrument) {
            const gcPaid =
                freshBasket.paymentInstruments
                    ?.filter((pi) => pi.paymentMethodId === PAYMENT_METHODS.GIFT_CERTIFICATE)
                    ?.reduce((sum, gc) => sum + (gc.amount || 0), 0) || 0

            await updatePaymentInstrumentInBasket({
                body: {amount: Math.max(0, freshBasket.orderTotal - gcPaid)},
                parameters: {
                    basketId: freshBasket.basketId,
                    paymentInstrumentId: boltInstrument.paymentInstrumentId
                }
            })
        }
    }

    const submitPromoCode = async ({code}) => {
        try {
            await applyPromoCodeMutation.mutateAsync({
                parameters: {basketId: basket?.basketId},
                body: {
                    code
                }
            })

            if (isBolt) {
                await updateBoltPaymentAmount()
            } else {
                localStorage.setItem('forceOpenPaymentToggle', true)
                setTimeout(() => {
                    localStorage.setItem('forceOpenPaymentToggle', false)
                }, 1000)
            }

            form.reset({code: ''})
            tagManager.applyPromoCode(code)
            toast({
                title: formatMessage({
                    defaultMessage: 'Promotion applied',
                    id: 'use_promocode.info.promo_applied'
                }),
                status: 'success',
                position: 'top-right',
                isClosable: true
            })
        } catch (e) {
            form.setError(
                'code',
                {
                    type: 'manual',
                    message: formatMessage({
                        defaultMessage:
                            "Oops! We can't find that coupon code. Please double-check that you've entered the correct code.",
                        id: 'use_promocode.error.check_the_code'
                    })
                },
                {shouldFocus: true}
            )
        }
    }

    const removePromoCode = async (couponItemId) => {
        await removePromoCodeMutation.mutateAsync(
            {
                parameters: {basketId: basket?.basketId, couponItemId}
            },
            {
                onSuccess: async () => {
                    const token = await getTokenWhenReady()
                    const parameters = {
                        c_currencyCode: currencyCodeContext,
                        c_countryISO: countryISOContext
                    }
                    await customapi.post(token, 'globale-prices', '/', parameters, proxy)
                    const body = {
                        c_refreshTimeStamp: new Date()
                    }
                    await updateBasket.mutate({
                        parameters: {basketId: basket.basketId},
                        body: body
                    })

                    toast({
                        title: formatMessage({
                            defaultMessage: 'Promotion removed',
                            id: 'use_promocode.info.promo_removed'
                        }),
                        status: 'success',
                        position: 'top-right',
                        isClosable: true
                    })
                },
                onError: () => {
                    toast({
                        title: formatMessage(API_ERROR_MESSAGE),
                        status: 'error',
                        position: 'top-right',
                        isClosable: true
                    })
                }
            }
        )

        if (isBolt) {
            await updateBoltPaymentAmount()
        } else {
            localStorage.setItem('forceOpenPaymentToggle', true)
            setTimeout(() => {
                localStorage.setItem('forceOpenPaymentToggle', false)
            }, 1000)
        }
    }

    return {form, submitPromoCode, removePromoCode}
}

export const PromoCode = ({
    form,
    submitPromoCode,
    itemProps,
    hasPromotions,
    hasBirthdayPromo,
    appliedPromos,
    birthdayPromoCard,
    isCheckout = false
}) => {
    const [isOpen, setOpen] = useState(!!birthdayPromoCard || !!hasPromotions)
    const {countryISO} = useGlobaleContext()
    const shouldRenderIDMe = !hasPromotions && (isCheckout || countryISO === 'US')
    const couponSection = useRef(null)

    useEffect(() => {
        if (form.formState.isSubmitSuccessful) {
            if (window.innerWidth <= 768) {
                setTimeout(() => {
                    const top =
                        couponSection.current.getBoundingClientRect().top + window.pageYOffset - 220

                    window.scrollTo({
                        top: top,
                        behavior: 'smooth'
                    })
                }, 300)
            }
        }
    }, [form.formState.isSubmitSuccessful])

    useEffect(() => {
        setOpen(!!birthdayPromoCard || !!hasPromotions)
    }, [birthdayPromoCard, hasPromotions])

    const CouponCodeStyles = useStyleConfig('CouponCodeStyles')

    return (
        <Accordion
            allowToggle
            index={isOpen ? 0 : -1}
            onChange={() => setOpen(!isOpen)}
            borderRadius="5px"
            border="1px solid #EDE8E3"
            className="promo-code-accordion"
            ref={couponSection}
        >
            <AccordionItem {...itemProps} border="none">
                {({isExpanded}) => (
                    <>
                        <AccordionButton
                            as={Button}
                            data-id="promo_code_dropdown_label"
                            {...CouponCodeStyles.accordionButton}
                            rightIcon={
                                isExpanded ? (
                                    <Image
                                        alt="Expand Icon"
                                        width="15px"
                                        height="15px"
                                        className="promo-code-accordion-image"
                                        color="black"
                                        verticalAlign="middle"
                                        src={getUrl('UIImages/images/minus-large.svg')}
                                    />
                                ) : (
                                    <Image
                                        alt="Expand Icon"
                                        width="15px"
                                        className="promo-code-accordion-image"
                                        height="15px"
                                        color="black"
                                        verticalAlign="middle"
                                        src={getUrl('UIImages/images/plus-large.svg')}
                                    />
                                )
                            }
                            onClick={() => form.reset()}
                        >
                            {hasPromotions ? (
                                <Flex align="center" gap="8px">
                                    <CheckIcon {...CouponCodeStyles.promoAppliedIcon} />
                                    <FormattedMessage
                                        defaultMessage="Promo code applied"
                                        id="order_summary.label.promotions_applied"
                                    />
                                </Flex>
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Do you have a promo code?"
                                    id="promocode.accordion.button.have_promocode"
                                />
                            )}
                        </AccordionButton>

                        <AccordionPanel
                            px={0}
                            sx={CouponCodeStyles.accordionPanel}
                            background={isCheckout ? 'transparent' : 'white'}
                        >
                            <Box
                                data-testid="promo-code-form"
                                data-id="promo_code_form"
                                as="form"
                                p={4}
                                background={isCheckout ? 'transparent' : 'white'}
                                borderRadius="base"
                                sx={{
                                    '#code-feedback': {
                                        width: 'calc(100% - 80px)'
                                    }
                                }}
                                onSubmit={form.handleSubmit(submitPromoCode)}
                            >
                                {hasPromotions ? (
                                    <>
                                        {appliedPromos}
                                        {birthdayPromoCard &&
                                            hasPromotions &&
                                            !hasBirthdayPromo && (
                                                <Box {...CouponCodeStyles.birthdayConflictMessage}>
                                                    <FormattedMessage
                                                        defaultMessage="Birthday Gift cannot be combined with other promo codes. Remove the promo code to apply your Birthday Gift."
                                                        id="promo.birthday.cannot.be.combined"
                                                    />
                                                </Box>
                                            )}
                                    </>
                                ) : (
                                    <PromoCodeFields form={form} />
                                )}
                                {birthdayPromoCard}
                                {shouldRenderIDMe && <IdmeButton />}
                            </Box>
                        </AccordionPanel>
                    </>
                )}
            </AccordionItem>
        </Accordion>
    )
}

PromoCode.propTypes = {
    /** The form object returned from `usePromoCode` hook */
    form: PropTypes.object.isRequired,

    /** The submit callback returned from `usePromoCode` hook */
    submitPromoCode: PropTypes.func.isRequired,

    /** Props applied to inner AccordionItem. Useful for style overrides. */
    itemProps: PropTypes.object,

    appliedPromos: PropTypes.node,

    hasPromotions: PropTypes.bool,

    hasBirthdayPromo: PropTypes.bool,

    birthdayPromoCard: PropTypes.node,

    isCheckout: PropTypes.bool
}

export default PromoCode
